<template>
  <div class="feedback-container">
    <div class="content-wrapper">
      <!-- Header -->
      <header class="page-header">
        <div class="header-content">
          <div class="header-icon-wrap">
            <v-icon size="28" color="white">mdi-comment-text-outline</v-icon>
          </div>
          <div class="header-text">
            <h1 class="page-title">Client Feedback</h1>
            <p class="page-subtitle">Monitor and analyze customer feedback</p>
          </div>
          <v-btn
            @click="downloadData"
            color="#8B0000"
            dark
            class="download-btn"
            elevation="0"
          >
            <v-icon left small>mdi-download</v-icon>
            Download Data
          </v-btn>
        </div>
      </header>

      <!-- Loading -->
      <div v-if="overlay" class="loading-container">
        <v-progress-circular indeterminate color="#8B0000" size="56" width="4" />
        <p class="loading-text">Loading feedback…</p>
      </div>

      <!-- Feedback Card -->
      <v-card v-if="!overlay" class="feedback-card" elevation="0">
        <v-card-text class="card-content">
          <!-- Search and Filters -->
          <div class="feedback-controls">
          <v-row align="center">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="Search feedback..."
                outlined
                dense
                clearable
                class="search-field"
              ></v-text-field>
            </v-col>
            
            <v-col cols="12" md="3">
              <v-select
                v-model="ratingFilter"
                :items="ratingOptions"
                label="Filter by Rating"
                outlined
                dense
                clearable
                class="filter-select"
              ></v-select>
            </v-col>
            
            <v-col cols="12" md="3">
              <v-select
                v-model="dateFilter"
                :items="dateFilterOptions"
                label="Filter by Date"
                outlined
                dense
                clearable
                class="filter-select"
              ></v-select>
            </v-col>
          </v-row>
        </div>

        <!-- Data Table -->
        <div class="table-wrapper">
          <v-data-table
            :headers="headers"
            :items="filteredFeedback"
            :search="search"
            class="modern-feedback-table"
            :items-per-page="15"
            :footer-props="{
              'items-per-page-options': [10, 15, 25, 50]
            }"
          >
            <!-- Date Column -->
            <template v-slot:[`item.created_at`]="{ item }">
              <div class="date-info">
                <div class="date">{{ formatDisplayDate(item.created_at) }}</div>
                <div class="time">{{ formatDisplayTime(item.created_at) }}</div>
              </div>
            </template>

            <!-- Rating Column -->
            <template v-slot:[`item.rating`]="{ item }">
              <div class="rating-display">
                <v-rating
                  :value="item.rating"
                  color="amber"
                  dense
                  half-increments
                  readonly
                  size="18"
                ></v-rating>
                <span class="rating-text">{{ item.rating }}/5</span>
              </div>
            </template>

            <!-- Comments Column -->
            <template v-slot:[`item.description`]="{ item }">
              <div class="comment-cell">
                <p class="comment-text">{{ truncateText(item.description, 100) }}</p>
                <v-btn
                  v-if="item.description && item.description.length > 100"
                  text
                  small
                  color="primary"
                  @click="showFullComment(item)"
                >
                  Read More
                </v-btn>
              </div>
            </template>

            <!-- Improve Column -->
            <template v-slot:[`item.name`]="{ item }">
              <v-chip
                :color="getImproveColor(item.name)"
                small
                class="improve-chip"
              >
                {{ item.name }}
              </v-chip>
            </template>
          </v-data-table>
        </div>
        </v-card-text>
      </v-card>

      <!-- Comment Dialog -->
      <v-dialog v-model="showCommentDialog" max-width="600px">
          <v-card class="comment-dialog">
            <v-card-title class="dialog-title">
              <v-icon left color="primary">mdi-comment-text</v-icon>
              Full Comment
            </v-card-title>
            
            <v-card-text class="comment-content">
              <p>{{ selectedComment }}</p>
            </v-card-text>
            
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="showCommentDialog = false">Close</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </div>
  </template>

<script>
import moment from 'moment'

export default {
  name: 'FeedbackPage',
  
  data() {
    return {
      headers: [
        { text: 'Date', value: 'created_at', sortable: true },
        { text: 'Area to Improve', value: 'name', sortable: true },
        { text: 'Comments', value: 'description', sortable: false },
        { text: 'Rating', value: 'rating', sortable: true },
      ],
      overlay: false,
      loading: false,
      search: '',
      ratingFilter: null,
      dateFilter: null,
      showCommentDialog: false,
      selectedComment: '',
      
      ratingOptions: [
        { text: '5 Stars', value: 5 },
        { text: '4 Stars', value: 4 },
        { text: '3 Stars', value: 3 },
        { text: '2 Stars', value: 2 },
        { text: '1 Star', value: 1 }
      ],
      
      dateFilterOptions: [
        { text: 'Today', value: 'today' },
        { text: 'This Week', value: 'week' },
        { text: 'This Month', value: 'month' },
        { text: 'Last 3 Months', value: '3months' }
      ]
    }
  },

  async created() {
    this.overlay = true;
    await this.$store.dispatch('dataModules/getFeedback')
    .then(() => {
      this.overlay = false;
    })
    .catch(error => {
      console.error('Error fetching feedback:', error);
      this.overlay = false;
    });
  },

  computed: {
    getFeedback() {
      return this.$store.getters['dataModules/getFeedback']
    },

    formattedFeedback() {
      return this.getFeedback.map(feedback => {
        return { 
          ...feedback, 
          original_date: feedback.created_at,
          created_at: feedback.created_at 
        };
      });
    },
    
    filteredFeedback() {
      let filtered = this.formattedFeedback;
      
      // Filter by rating
      if (this.ratingFilter !== null) {
        filtered = filtered.filter(item => item.rating === this.ratingFilter);
      }
      
      // Filter by date
      if (this.dateFilter) {
        const now = new Date();
        filtered = filtered.filter(item => {
          const itemDate = new Date(item.original_date);
          
          switch (this.dateFilter) {
            case 'today':
              return itemDate.toDateString() === now.toDateString();
            case 'week':
              const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
              return itemDate >= weekAgo;
            case 'month':
              const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
              return itemDate >= monthAgo;
            case '3months':
              const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
              return itemDate >= threeMonthsAgo;
            default:
              return true;
          }
        });
      }
      
      return filtered;
    }
  },

  methods: {
    format_date(value) {
      if (value) {
        return moment(String(value)).format('YYYY-MM-DD')
      }
    },
    
    formatDisplayDate(value) {
      if (value) {
        return moment(String(value)).format('MMM DD, YYYY')
      }
      return '';
    },
    
    formatDisplayTime(value) {
      if (value) {
        return moment(String(value)).format('HH:mm')
      }
      return '';
    },
    
    truncateText(text, length) {
      if (!text) return '';
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    },
    
    showFullComment(item) {
      this.selectedComment = item.description;
      this.showCommentDialog = true;
    },
    
    getImproveColor(improvementArea) {
      const colors = {
        'UI/UX': 'blue',
        'Performance': 'orange',
        'Features': 'green',
        'Support': 'purple',
        'Documentation': 'teal',
        'Bug Fix': 'red',
        'General': 'grey'
      };
      
      // Try to match common improvement areas
      for (const [key, color] of Object.entries(colors)) {
        if (improvementArea && improvementArea.toLowerCase().includes(key.toLowerCase())) {
          return color;
        }
      }
      
      return 'primary';
    },

    downloadData() {
      // Convert your filtered data to CSV
      const csvContent = this.convertToCSV(this.filteredFeedback);
      
      // Create a blob from the CSV content
      const blob = new Blob([csvContent], { type: 'text/csv' });
      
      // Create a link element
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      
      // Set the filename for the download with timestamp
      const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
      link.download = `feedback_data_${timestamp}.csv`;
      
      // Simulate click on the link to trigger the download
      link.click();
    },
    
    convertToCSV(data) {
      if (!data || data.length === 0) return '';
      
      // Get headers
      const headers = ['Date', 'Area to Improve', 'Comments', 'Rating'];
      
      // Convert data to CSV format
      const csvRows = [
        headers.join(','),
        ...data.map(row => [
          this.formatDisplayDate(row.created_at),
          `"${row.name || ''}"`,
          `"${(row.description || '').replace(/"/g, '""')}"`,
          row.rating || ''
        ].join(','))
      ];
      
      return csvRows.join('\n');
    }
  }
  }
</script>

<style lang="scss" scoped>
$red-primary: #8B0000;
$red-dark: #5a0000;

.feedback-container {
  min-height: 100vh;
  padding: 12px 0 40px;
}

.content-wrapper {
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: 960px) {
    padding: 0 12px;
  }
}

/* ---------- Header ---------- */
.page-header {
  margin-bottom: 16px;
  padding-top: 12px;

  .header-content {
    display: flex;
    align-items: center;
    gap: 16px;

    @media (max-width: 600px) {
      flex-wrap: wrap;
    }
  }

  .header-icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: linear-gradient(135deg, $red-primary, $red-dark);
    box-shadow: 0 10px 24px rgba(139, 0, 0, 0.28);
    flex-shrink: 0;
  }

  .header-text {
    flex: 1;
    min-width: 0;
  }

  .page-title {
    font-size: 1.4rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    letter-spacing: -0.02em;
  }

  .page-subtitle {
    font-size: 0.85rem;
    color: #64748b;
    margin: 2px 0 0;
  }

  .download-btn {
    border-radius: 12px !important;
    font-weight: 600 !important;
    text-transform: none !important;
    letter-spacing: 0.5px;
    background: linear-gradient(135deg, $red-primary, $red-dark) !important;
    box-shadow: 0 10px 24px rgba(139, 0, 0, 0.28) !important;
    transition: all 0.25s ease;

    &:hover {
      box-shadow: 0 12px 28px rgba(139, 0, 0, 0.35) !important;
      transform: translateY(-2px);
    }
  }
}

/* Loading */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 20px;

  .loading-text {
    font-size: 1rem;
    color: #7f8c8d;
  }
}

.feedback-card {
  border-radius: 20px !important;
  background: rgba(255, 255, 255, 0.85) !important;
  backdrop-filter: blur(14px);
  border: 1px solid rgba(226, 232, 240, 0.9);
  box-shadow: 0 10px 40px rgba(15, 23, 42, 0.06) !important;
  margin-bottom: 16px;
  overflow: hidden;

  .card-content {
    padding: 16px;

    @media (max-width: 960px) {
      padding: 12px;
    }
  }
}

.feedback-controls {
  margin-bottom: 16px;
  
  .search-field, .filter-select {
    ::v-deep .v-input__slot {
      border-radius: 12px !important;
      background: #fbfcfe !important;
    }
  }
}

.table-wrapper {
  overflow: visible;
}

.modern-feedback-table {
  border-radius: 16px;
  overflow: hidden;
  
  ::v-deep .v-data-table__wrapper {
    border-radius: 16px 16px 0 0;
    overflow-y: auto !important;
    overflow-x: auto;
    max-height: calc(100vh - 420px);
  }
  
  ::v-deep .v-data-table-header {
    background: linear-gradient(135deg, $red-primary, $red-dark);
    position: sticky;
    top: 0;
    z-index: 10;
    
    th {
      color: white !important;
      font-weight: 600 !important;
      border-bottom: none !important;
      font-size: 0.875rem !important;
      background: linear-gradient(135deg, $red-primary, $red-dark) !important;
    }
  }
  
  ::v-deep tbody tr {
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(139, 0, 0, 0.04) !important;
    }
  }
  
  ::v-deep .v-data-footer {
    flex-shrink: 0;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
  }
}

.date-info {
  .date {
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 2px;
    font-size: 0.875rem;
  }
  
  .time {
    font-size: 0.75rem;
    color: #7f8c8d;
  }
}

.rating-display {
  display: flex;
  align-items: center;
  gap: 8px;
  
  .rating-text {
    font-weight: 500;
    color: #2c3e50;
    font-size: 0.875rem;
  }
}

.comment-cell {
  max-width: 300px;
  
  .comment-text {
    margin: 0 0 4px 0;
    line-height: 1.4;
    color: #2c3e50;
    font-size: 0.875rem;
  }
}

.improve-chip {
  font-weight: 500 !important;
  font-size: 0.75rem !important;
}

.comment-dialog {
  border-radius: 16px;
  
  .dialog-title {
    background: linear-gradient(135deg, $red-primary, $red-dark);
    color: white;
    font-weight: 600;
  }
  
  .comment-content {
    padding: 24px;
    
    p {
      line-height: 1.6;
      color: #2c3e50;
      margin: 0;
    }
  }
}

// Responsive design
@media (max-width: 768px) {
  .page-header {
    .header-content {
      flex-direction: column;
      align-items: stretch;
      
      .download-btn {
        align-self: flex-start;
      }
    }
  }
  
  .comment-cell {
    max-width: 200px;
  }
}

@media (max-width: 600px) {
  .modern-feedback-table {
    ::v-deep .v-data-table__wrapper {
      overflow-x: auto;
    }
  }
  
  .page-title {
    font-size: 1.2rem !important;
  }
}
</style>